import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MQ = "(max-width: 767px)";
const ZoomContext = createContext(false);
export const useInZoom = () => useContext(ZoomContext);
export const ScreenPortal = ({ children }) => (useInZoom() ? createPortal(children, document.body) : children);

export function useIsMobile() {
  const [m, setM] = useState(() => window.matchMedia(MQ).matches);
  useEffect(() => {
    const q = window.matchMedia(MQ);
    const f = () => setM(q.matches);
    q.addEventListener("change", f);
    return () => q.removeEventListener("change", f);
  }, []);
  return m;
}

function useHeaderHeight() {
  const [h, setH] = useState(0);
  useLayoutEffect(() => {
    const el = document.querySelector('[data-testid="top-menu"]');
    if (!el) return;
    window.scrollTo(0, 0);
    const measure = () => setH(Math.max(0, Math.round(el.getBoundingClientRect().bottom)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, []);
  return h;
}

function usePinch(outer, inner, st, apply) {
  useEffect(() => {
    const o = outer.current, el = inner.current;
    let g = null, raf = 0;
    const pts = (e) => {
      const r = o.getBoundingClientRect();
      const [a, b] = e.touches;
      return { d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), fx: (a.clientX + b.clientX) / 2 - r.left, fy: (a.clientY + b.clientY) / 2 - r.top };
    };
    const scaleFor = (p) => {
      const S = st.current;
      return Math.min(Math.max(S.fit * 4, 1.5), Math.max(S.fit, g.s * (p.d / g.d)));
    };
    // During the gesture only the composited transform changes; layout is committed on release.
    const clampPos = (v, size, view) => (size <= view ? (view - size) / 2 : Math.min(0, Math.max(view - size, v)));
    const frame = () => {
      raf = 0;
      if (!g || !g.p) return;
      const ns = scaleFor(g.p), S = st.current;
      g.ns = ns;
      g.vx = clampPos(g.p.fx - g.cx * ns, S.w * ns, o.clientWidth);
      g.vy = S.h * ns <= o.clientHeight ? 0 : clampPos(g.p.fy - g.cy * ns, S.h * ns, o.clientHeight);
      el.style.transform = `translate(${g.sl + g.vx}px, ${g.st + g.vy}px) scale(${ns})`;
    };
    const start = (e) => {
      if (e.touches.length !== 2) return;
      const p = pts(e), S = st.current;
      S.pinching = true;
      g = { d: p.d, s: S.s, ns: S.s, p, sl: o.scrollLeft, st: o.scrollTop,
            cx: (o.scrollLeft + p.fx - S.ox) / S.s, cy: (o.scrollTop + p.fy - S.oy) / S.s };
      el.style.willChange = "transform";
    };
    const move = (e) => {
      if (!g || e.touches.length !== 2) return;
      e.preventDefault();
      g.p = pts(e);
      if (!raf) raf = requestAnimationFrame(frame);
    };
    const end = (e) => {
      if (!g || e.touches.length >= 2) return;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      const S = st.current, { ns, vx, vy } = g;
      const moved = vx !== undefined;
      g = null;
      S.user = ns > S.fit + 0.001;
      apply(ns);
      if (moved) {
        o.scrollLeft = S.ox - vx;
        o.scrollTop = S.oy - vy;
      }
      el.style.willChange = "";
      S.pinching = false;
    };
    o.addEventListener("touchstart", start, { passive: true });
    o.addEventListener("touchmove", move, { passive: false });
    o.addEventListener("touchend", end);
    o.addEventListener("touchcancel", end);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      o.removeEventListener("touchstart", start);
      o.removeEventListener("touchmove", move);
      o.removeEventListener("touchend", end);
      o.removeEventListener("touchcancel", end);
    };
  }, [outer, inner, st, apply]);
}

function ZoomCanvas({ width, unitHeight, children }) {
  const outer = useRef(null), spacer = useRef(null), inner = useRef(null);
  const st = useRef({ s: 1, fit: 1, user: false, h: 0, ox: 0, oy: 0 });
  const hdr = useHeaderHeight();
  const apply = useRef((s) => {
    const o = outer.current, S = st.current;
    S.s = s;
    S.w = width;
    const w = width * s, h = S.h * s;
    S.ox = Math.max(0, (o.clientWidth - w) / 2);
    S.oy = 0;
    spacer.current.style.width = `${w + S.ox}px`;
    spacer.current.style.height = `${h + S.oy}px`;
    inner.current.style.transform = `translate(${S.ox}px, ${S.oy}px) scale(${s})`;
  }).current;

  useLayoutEffect(() => {
    let raf = 0;
    const refit = () => {
      raf = 0;
      const o = outer.current, S = st.current;
      if (!o || S.pinching) return;
      S.h = inner.current.offsetHeight || 1;
      S.fit = Math.min(o.clientWidth / width, o.clientHeight / S.h);
      if (!S.user || S.s < S.fit) { S.user = false; apply(S.fit); } else apply(S.s);
    };
    refit();
    const ro = new ResizeObserver(() => { if (!raf) raf = requestAnimationFrame(refit); });
    ro.observe(inner.current);
    ro.observe(outer.current);
    return () => { ro.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [width, apply]);

  usePinch(outer, inner, st, apply);

  return (
    <div ref={outer} data-testid="mobile-zoom-viewport" className="fixed inset-x-0 bottom-0 overflow-auto"
         style={{ top: hdr, touchAction: "pan-x pan-y", overscrollBehavior: "contain" }}>
      <div ref={spacer} className="relative">
        <div ref={inner} data-testid="mobile-zoom-canvas" className="absolute top-0 left-0"
             style={{ width, transformOrigin: "0 0", "--vw": `${width / 100}px`, "--vh": `${unitHeight / 100}px` }}>
          <ZoomContext.Provider value={true}>{children}</ZoomContext.Provider>
        </div>
      </div>
    </div>
  );
}

export default function MobileZoom({ width, unitHeight, children }) {
  const mobile = useIsMobile();
  if (!mobile) return children;
  return <ZoomCanvas width={width} unitHeight={unitHeight}>{children}</ZoomCanvas>;
}
